import { Component, useEffect, type ErrorInfo, type ReactNode } from 'react';
import AppRouter from './AppRouter';

const loadTelemetry = () => import('../sentry');

function reportClientError(error: Error, componentStack?: string | null) {
  void fetch('/api/client-error', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message: error.message,
      stack: error.stack || '',
      componentStack: componentStack || '',
      url: window.location.href,
      release: import.meta.env.VITE_RELEASE || 'unknown',
    }),
    keepalive: true,
  }).catch(() => {});
}

class StartupBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    reportClientError(error, info.componentStack);
    if (import.meta.env.VITE_SENTRY_DSN_FRONTEND) {
      void loadTelemetry().then(({ Sentry }) => Sentry.captureException(error, {
        contexts: { react: { componentStack: info.componentStack } },
      })).catch(err => console.warn('[telemetry] error reporting failed', err));
    }
  }
  render() {
    return this.state.failed ? (
      <div style={{ padding: 24, color: '#fff', maxWidth: 420, margin: '15vh auto 0', textAlign: 'center' }}>
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>Something went wrong</div>
        <div style={{ fontSize: 14, opacity: 0.8, lineHeight: 1.5, marginBottom: 16 }}>
          The app hit an unexpected error. Reloading usually fixes it.
        </div>
        <button type="button" onClick={() => window.location.reload()}
          style={{ padding: '10px 22px', fontSize: 15, fontWeight: 600, borderRadius: 10, border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer' }}>
          Reload
        </button>
      </div>
    ) : this.props.children;
  }
}

function BootCommitted() {
  useEffect(() => {
    const el = document.getElementById('boot-skeleton');
    if (el) requestAnimationFrame(() => requestAnimationFrame(() => {
      el.classList.add('boot-hide');
      window.setTimeout(() => el.remove(), 240);
    }));

    if (!import.meta.env.VITE_SENTRY_DSN_FRONTEND) return;
    let idle: number | undefined;
    let timer: number | undefined;
    const run = () => { void loadTelemetry().catch(err => console.warn('[telemetry] initialization failed', err)); };
    const initialize = () => {
      if (typeof window.requestIdleCallback === 'function') idle = window.requestIdleCallback(run, { timeout: 5000 });
      else timer = window.setTimeout(run, 1000);
    };
    if (document.readyState === 'complete') initialize();
    else window.addEventListener('load', initialize, { once: true });
    return () => {
      window.removeEventListener('load', initialize);
      if (idle !== undefined) window.cancelIdleCallback(idle);
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, []);
  return null;
}

/** Components live outside the entry module so Fast Refresh has a boundary.
 * Crash handling is immediate; optional telemetry never blocks first render. */
export default function BootRoot() {
  return <StartupBoundary><AppRouter /><BootCommitted /></StartupBoundary>;
}